import { FlexiMaker } from '@modules/NewMaker/FlexiMaker/F/Function/fleximaker.ts';
import { Stickerizer } from '@modules/Vector/F/Function/stickerizer/stickerizer.ts';
import { BpOptionConstantS } from '@modules/BpOption/S/Constant/module/BpOptionConstantS.js';
import { SVGEditorUtilF } from '@modules/SVGEditor/Util/F/Function/module/SVGEditorUtilF.js';
import { each, go, mapC, noop, sel } from 'fxjs/es';
import {
  FlexiMakerDomUtils,
  FlexiMakerUtils,
} from '@modules/NewMaker/FlexiMaker/F/Function/flexmaker.helper.ts';
import { MATERIAL_TYPE, OFFSET_BP_OPTION_GROUP } from '@modules/NewMaker/FlexiMaker/S/Constant/fleximaker.js';
import { $addClass, $delegate, $find, $on, $qs, $removeClass } from 'fxdom/es';
import { UtilF } from '@modules/Util/F/Function/module/UtilF.js';
import { SizeGauge } from '@modules/NewMaker/UI/sizeGauge/SizeGauge.ts';
import { rune } from 'rune-ts';
import { MobileAddToCart } from '@services/Marpple/client/shared/ui/cells/MobileAddToCart/MobileAddToCart.ts';
import {
  ApiAbortError,
  ApiNetworkError,
  ApiRequestError,
  ApiTimeoutError,
  InvalidOriginalImageSizeLimitError,
} from '@modules/Vector/F/Function/stickerizer/stickerizer.error.ts';
import { getCvDesigns } from '@modules/Maker/F/Fcanvas/cv_object.js';
import { getProductColorInMaker } from '@modules/Maker/F/getSth.js';
import { processAfterSelect } from '@modules/BpOption/F/Function/bpoption.js';
import { OMPCoreUtilF } from '@modules/OMP/Core/Util/F/Function/module/OMPCoreUtilF.js';
import {
  MinMaxScaleInvalidError,
  MinScaleFitError,
  NormalizedItemSizeNotInitializedError,
  NoViableBoxError,
} from '@modules/Vector/F/Function/sizeCalculator/sizeCalculator.error.ts';
import { errorHandlingEventWrapper } from '@modules/NewMaker/Error/F/Function/errorHandlingEventWrapper.js';
import { decorationOff } from '@modules/NewMaker/IconMenu/F/Function/decorationEvent.js';
import { NewMakerCvObjectCvImageF } from '@modules/NewMaker/CvObject/CvImage/F/Function/module/NewMakerCvObjectCvImageF.js';
import { OMPSizeGaugeF } from '@modules/NewMaker/SizeGauge/F/Function/module/OMPSizeGaugeF.js';
import { errorHandling } from '@modules/BpOption/F/Function/event.js';
import { showSpecialMessage } from '@modules/Maker/F/special_message.js';
import { renderVisibleWhenSelected } from '@modules/Maker/F/select_faces.js';

export const FlexiAPI = {
  getInstance() {
    return FlexiMaker.getInstance();
  },

  isFlexiProduct(base_product) {
    return base_product?.maker_type === BpOptionConstantS.UI_TYPE.FLEXI;
  },

  flexiMaker() {
    return G.mp.maker.flexiMaker;
  },

  getFlexiMakerCvObj: () => {
    const fcanvas = window.box().maker.canvass[0];
    return getCvDesigns(fcanvas._objects)[0];
  },

  async initializeFlexiMaker() {
    const pc = box().maker.product_color;
    const bp_id = pc.base_product_id;
    if (pc.svg_product_id) {
      const cv_image = this.getFlexiMakerCvObj();
      const stickerizer = await this.createStickerizer(cv_image, bp_id);

      // sizeGauge가 나타나기 위한 셋팅 (실제 디자인은 restore())
      return FlexiMaker.getInstance({ cv_image, stickerizer, bp_id });
    } else {
      return FlexiMaker.getInstance({ bp_id });
    }
  },

  async restore() {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    if (flexiMaker.hasImage) {
      FlexiMakerDomUtils.switchUploadIcon(flexiMaker.hasImage);
      await flexiMaker.setImageUrl(flexiMaker.data.cv_image, {
        createInitialSize: false,
        stickerizer: flexiMaker.stickerizer,
        force_process: true,
      });
      if (!UtilF.isLegacyMobile()) {
        const canvas = G.mp.maker.editing_canvas();
        if (canvas) {
          renderVisibleWhenSelected(canvas);
          await showSpecialMessage(canvas);
        }
      }
    }
  },

  async render(canvas) {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    canvas.appendChild(flexiMaker.render());

    if (!UtilF.isLegacyMobile()) {
      requestAnimationFrame(() => {
        flexiMaker.setSizeGuide();
      });
    }
  },

  async createStickerizer(cv_image, bp_id) {
    try {
      const baseProductId = bp_id ?? this.flexiMaker()?.bpId;
      const stickerizer = await Stickerizer.create({
        image_url: `https:${cv_image._data.image_url}`,
        bp_id: baseProductId,
      });

      return stickerizer;
    } catch (error) {
      if (error instanceof ApiAbortError) {
        console.warn('API 요청이 중단됨', error);
        return null;
      }

      if (error instanceof ApiNetworkError || error instanceof ApiTimeoutError) {
        console.warn('네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.');
        return null;
      }

      if (error instanceof ApiRequestError) {
        throw error;
      }
      throw error;
    }
  },

  async updateSize(product, canvas_zoom_wrapper, size_info) {
    let loaderDone = null;
    try {
      const flexiMaker = this.flexiMaker();
      if (!flexiMaker) return;

      const size = flexiMaker.getSizeFormSliderValue(size_info.slider_value);

      const isCached = flexiMaker.isCahcedSize(size_info.slider_value);

      if (!isCached) {
        loaderDone = SVGEditorUtilF.percentLoader({
          message: TT('flexi_maker::loading'),
          time: 10 * 1000,
          clazz: 'pc',
        });
      }

      const realSize = await flexiMaker.processStickerizer(size, size_info.slider_value);

      flexiMaker.redraw();
      flexiMaker.updateCanvasScale(
        canvas_zoom_wrapper,
        { ...size_info, size: { ...realSize } },
        G.mp.maker.zoom.make_translateXY,
      );

      flexiMaker.setSizeGuide();
      const canvas = G.mp.maker.editing_canvas();
      if (canvas) {
        renderVisibleWhenSelected(canvas);
        await showSpecialMessage(canvas);
      }

      return realSize;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (loaderDone) await loaderDone().catch(noop);
    }
  },

  getOffsetDistanceMm(product) {
    const selected_bp_option_ids = product._.selected_option_group.bp_option_ids;

    if (!selected_bp_option_ids) return;

    const custom_option = this.flexiMaker().profileCustomOption;
    if (custom_option) {
      return custom_option.offset;
    }
    const offset_option = OFFSET_BP_OPTION_GROUP.bp_option.find((option) =>
      selected_bp_option_ids.includes(option.id),
    );

    return offset_option?.value;
  },

  getMeterialType(product) {
    const selected_bp_option_ids = product._.selected_option_group.bp_option_ids;

    if (!selected_bp_option_ids) return;

    const custom_option = this.flexiMaker().profileCustomOption;
    if (custom_option) {
      return custom_option.materialSource;
    }

    const metrial_option = MATERIAL_TYPE.bp_option.find((option) =>
      selected_bp_option_ids.includes(option.id),
    );

    return metrial_option;
  },

  async processFlexiMakerImage(cv_image, product, options = { createInitialSize: true }) {
    let loaderDone = null;
    try {
      const flexiMaker = this.flexiMaker();
      if (!flexiMaker) return;

      /*오프셋 저장*/
      const offsetDistanceMm = this.getOffsetDistanceMm(product);
      FlexiMakerUtils.setCvImageSize(cv_image, { offset: offsetDistanceMm });

      /* 재질 저장*/
      const materialInfo = this.getMeterialType(product);
      FlexiMakerUtils.setCvImageSize(cv_image, { meterial: materialInfo });

      /* 로더 여부 확인 */
      let needLoader = false;

      if (options.createInitialSize) {
        needLoader = true;
      } else {
        needLoader = !flexiMaker.isCahcedSize(flexiMaker.sliderValue);
      }

      if (needLoader) {
        loaderDone = SVGEditorUtilF.percentLoader({
          message: TT('flexi_maker::loading'),
          time: 10 * 1000,
          clazz: 'pc',
        });
      }

      const stickerizer = options.createInitialSize
        ? await this.createStickerizer(cv_image, flexiMaker.bpId)
        : flexiMaker.stickerizer;

      /* 이미지 저장*/
      const realSize = await flexiMaker.setImageUrl(cv_image, {
        ...options,
        stickerizer,
        force_process: true,
      });

      const is_maker_editor_mode = document.querySelector('.maker_editor_mode_for_canvas');

      if (OMPCoreUtilF.isMobile() && !is_maker_editor_mode) {
        return realSize;
      }
      requestAnimationFrame(() => flexiMaker.setSizeGuide());
      const canvas = G.mp.maker.editing_canvas();

      if (canvas) {
        renderVisibleWhenSelected(canvas);
        await showSpecialMessage(canvas);
      }

      return realSize;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (loaderDone) await loaderDone().catch(noop);
    }
  },

  createSizeOption(bp_option_group) {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    if (!flexiMaker || !this.isFlexiSizeOptionGroup(bp_option_group)) {
      return null;
    }

    return {
      option_group_name: bp_option_group.name,
      option_group_name_en: bp_option_group.name_en,
      option_group_name_jp: bp_option_group.name_jp,
      option_name: flexiMaker.sizeStr,
      option_name_en: flexiMaker.sizeStr,
      option_name_jp: flexiMaker.sizeStr,
      is_name: bp_option_group.is_name,
      no: bp_option_group.no,
    };
  },

  isFlexiSizeOptionGroup(bp_option_group) {
    return (
      FlexiMaker.getInstance() &&
      // bp_option_group.dev_name === '조각스티커_사이즈' &&
      bp_option_group.ui_type_pc === BpOptionConstantS.UI_TYPE.FLEXI
    );
  },

  handleIcon() {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    const flexi_icon = $qs('.flexi_icon');
    if (flexiMaker?.hasImage) {
      $removeClass('disabled', flexi_icon);
    } else {
      $addClass('disabled', flexi_icon);
    }
  },

  async uploadThumbnail(product_color) {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    await go(
      product_color.product_faces2.value,
      mapC(async (pf) => {
        const data_url = await flexiMaker.getThumbnailDataURL();
        pf.preview_thumbnail_url = await go($.uploadFileToUrl(data_url, 'preview_image', 'png'), sel('url'));
      }),
    );
  },

  setSizeGuide() {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    flexiMaker.setSizeGuide();
  },

  makeSizeGaugeHtml() {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    const {
      imageUrl,
      imageSize: { width, height },
      sizeRange,
      sliderValue,
    } = flexiMaker;
    const disabled = !imageUrl;

    return new SizeGauge({
      width,
      height,
      is_mobile: false,
      disabled,
      custom_klass: disabled ? 'disabled' : '',
      hide_slider: false,
      size_range: sizeRange,
      slider_value: sliderValue,
    }).toHtmlSSR();
  },

  makeSizeName({ is_mobile, is_designed, flexi_size }) {
    if (is_mobile && !is_designed) {
      return TT('flexi_maker::design_required');
    }

    if (is_designed && flexi_size) {
      return `${Math.round(flexi_size.width)} x ${Math.round(flexi_size.height)} mm`;
    }
  },

  getBpOptionIdByOffset(offset) {
    const offset_option = OFFSET_BP_OPTION_GROUP.bp_option.find((option) => offset == option.value);
    return offset_option?.id;
  },

  syncAddToCartButtonState(view) {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    view?.disableAddToCart(!flexiMaker.hasImage);
  },

  async updateFlexiMakerByProductOption(product_color) {
    try {
      const flexiMaker = this.flexiMaker();
      if (!flexiMaker) return;

      const cv_image = flexiMaker.data.cv_image;
      if (!cv_image) {
        return;
      }

      const real_size = await this.processFlexiMakerImage(cv_image, product_color, {
        createInitialSize: false,
      });

      if (!real_size) return;

      return real_size;
    } catch (e) {
      if (
        e instanceof MinScaleFitError ||
        e instanceof NormalizedItemSizeNotInitializedError ||
        e instanceof NoViableBoxError ||
        e instanceof MinMaxScaleInvalidError
      ) {
        return OMPCoreUtilF.titleAlert({
          title: TT('flexi_maker::alert::image_error::title'),
          content: TT('flexi_maker::alert::image_error::content'),
        });
      }

      if (e instanceof InvalidOriginalImageSizeLimitError) {
        return OMPCoreUtilF.titleAlert({
          title: TT('flexi_maker::alert::size_error::title'),
          content: TT('flexi_maker::alert::size_error::content'),
        });
      }

      $.alert('이미지 업로드 중 오류가 발생했습니다.');
      console.error(e);
    }
  },

  syncCanvasScale() {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    const { imageSize: size, sliderValue } = flexiMaker;

    if (sliderValue !== undefined || sliderValue !== null) {
      const canvas_zoom_wrapper = $qs('.canvas_zoom_wrapper');
      flexiMaker.updateCanvasScale(
        canvas_zoom_wrapper,
        {
          size,
          slider_value: sliderValue,
        },
        G.mp.maker.zoom.make_translateXY,
      );
    }
  },

  async makeMaterials(product) {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    await flexiMaker.makeMaterials(product);
  },

  afterCloseFlexiMakerMo() {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;

    FlexiMakerDomUtils.toggleMoblieDescription(false);

    const mobileAddToCartEl = document.querySelector('.MobileAddToCart');

    if (mobileAddToCartEl) {
      const mobileAddToCart = rune.getView(mobileAddToCartEl, MobileAddToCart);
      this.syncAddToCartButtonState(mobileAddToCart);
    }

    if (box.sel('shared_product')) {
      if (G.mp.maker.is_shared_product_design_changed) G.mp.maker.is_shared_product_design_changed();
    }
  },

  handleRangeSizeValue(slider_value) {
    const flexiMaker = this.flexiMaker();
    if (!flexiMaker) return;
    return flexiMaker.handleRangeSizeValue(slider_value);
  },

  async handelNewImageUpload(cv_image) {
    try {
      const product = getProductColorInMaker();

      await this.processFlexiMakerImage(cv_image, product, { createInitialSize: true });

      this.syncCanvasScale();
      requestAnimationFrame(() => this.setSizeGuide()); // 스케일 변경으로 인해 사이즈 다시 세팅 해줘야함
      // const dev_name = this.flexiMaker().profileCustomOption?.sizeOptionDevName ?? '조각스티커_사이즈';
      const bp_option_group_el = go($qs(`.bp_option_group[data-ui_type_pc="FLEXI"]`), $find('select'));

      await errorHandling(async () => {
        await processAfterSelect(bp_option_group_el); // 사이즈 옵션으로 가격 결정
      });

      if (OMPCoreUtilF.isMobile()) {
        $.removeClass($1('.flexi_icon'), 'disabled');
      } else {
        G.mp.maker.unselect_all();
      }

      const canvas = G.mp.maker.editing_canvas();
      if (canvas) {
        renderVisibleWhenSelected(canvas);
        await showSpecialMessage(canvas);
      }
    } catch (e) {
      if (e instanceof Error && e.message.includes('Invalid SVG: path without d')) {
        return $.alert(TT('flexi_maker::alert::image_error::title'));
      }

      if (
        e instanceof MinScaleFitError ||
        e instanceof NormalizedItemSizeNotInitializedError ||
        e instanceof NoViableBoxError ||
        e instanceof MinMaxScaleInvalidError
      ) {
        return OMPCoreUtilF.titleAlert({
          title: TT('flexi_maker::alert::image_error::title'),
          content: TT('flexi_maker::alert::image_error::content'),
        });
      }

      if (e instanceof InvalidOriginalImageSizeLimitError) {
        return OMPCoreUtilF.titleAlert({
          title: TT('flexi_maker::alert::size_error::title'),
          content: TT('flexi_maker::alert::size_error::content'),
        });
      }

      $.alert('이미지 업로드 중 오류가 발생했습니다.');
      console.error(e);
    }
  },
  events(el) {
    if (!this.flexiMaker()) return;
    go(
      el,
      $delegate(
        'click',
        '.mp_maker .flexi-maker-preview__upload-icon, .flexi_icon .my_lib',
        errorHandlingEventWrapper(function (e) {
          decorationOff();
          $.frame.open({
            frame_name: 'maker.image_upload_frame',
            closing: async function (X, image) {
              if (!image) return;
              if (image.original_url && image.original_url.includes('.ai')) {
                $.alert(TT('flexi_maker::not_allowed'));
                return;
              }
              go(
                G.mp.maker.designs(),
                each((cv_image) => cv_image.remove()),
              );
              const cv_image = await NewMakerCvObjectCvImageF.addCvImageToMpMaker(image);
              // mo 직접 업로드
              if (G.mp.maker.flexiApi) {
                $.don_loader_end();
                await G.mp.maker.flexiApi.handelNewImageUpload(cv_image);
              }
            },
          });
        }),
      ),
      $delegate('click', '.mp_maker .flexi_icon .size', () => {
        // Mobile
        const flexiMaker = this.flexiMaker();
        const {
          imageSize: { width, height },
          sizeRange,
          sliderValue,
        } = flexiMaker;

        OMPSizeGaugeF.openSizeGauge({
          width,
          height,
          is_mobile: true,
          hide_slider: false,
          size_range: sizeRange,
          slider_value: sliderValue,
        });
      }),
      $on(
        'mouseenter',
        '.mp_maker .upload_icon_wrap .my_lib',
        errorHandlingEventWrapper(function (e) {
          const image_up_desc = $1('.image_upload_desc');
          const menu = $.closest(e.currentTarget, '.upload_icon_wrap');

          const has_image = $.hasClass(menu, 'has_image');

          const css_attr = {
            width: 358,
            'min-width': 358,
            top: $.position(menu).top + $.position(e.currentTarget).top - 10 + 130,
            left: $.position(e.currentTarget).left - 119,
            ...(G.collabo_type == '' ? { 'min-height': 'fit-content' } : {}),
          };

          if (has_image) {
            $.addClass(image_up_desc, 'has_image');
            image_up_desc.style.cssText = '';
            $.css(image_up_desc, { top: $.position(menu).top + 8 });
          } else {
            $.removeClass(image_up_desc, 'has_image');
            $.css(image_up_desc, css_attr);
            if (G.collabo_type !== '') {
              image_up_desc.style.setProperty('min-height', 'fit-content', 'important');
            }
          }

          const tit1_element = image_up_desc.querySelector('.tit1');
          if (tit1_element) {
            tit1_element.textContent = TT('flexi_maker::upload::desc');
          }

          $.attr(image_up_desc, 'is_enter', true);
          $.show(image_up_desc);
        }),
      ),
      $on(
        'mouseleave',
        '.mp_maker .upload_icon_wrap .my_lib',
        errorHandlingEventWrapper(function (e) {
          const image_up_desc = $1('.image_upload_desc');
          $.attr(image_up_desc, 'is_enter', 'false');
          setTimeout(function () {
            if ($.attr(image_up_desc, 'is_enter') == true) return;
            $.hide(image_up_desc);
          }, 2000);
        }),
      ),
      $delegate(
        'click',
        '.mp_maker .upload_icon_wrap .my_lib .icon',
        errorHandlingEventWrapper(function (e) {
          const target_el = $.find1($.closest(e.currentTarget, '.board'), '.my_lib');

          $.trigger(target_el, 'click');
        }),
      ),
    );
  },
};
